/**
 * MessageFlowTab
 *
 * Live "mini RabbitMQ management UI" — shows whether messages are being published
 * and consumed. Cluster-wide publish/deliver/ack rates plus a per-queue table with
 * ready/unacked/consumer counts and rates. Each queue can be inspected to view the
 * messages parked in it (payload + who published them).
 */

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import {
  IconRefresh,
  IconEye,
  IconAlertCircle,
  IconArrowUp,
  IconArrowDown,
  IconCircleCheck,
  IconRepeat,
  IconPlayerPlay,
  IconPlayerPause,
} from '@tabler/icons-react';
import { Stack, Group, Text, Grid, Card, Badge, Button, Alert, Loader, ActionIcon } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { useMessageFlow } from '../hooks/useMessageFlow';
import { MessageInspectorModal } from './MessageInspectorModal';
import type { QueueFlowStats, PeekResult } from '../types';

const REFRESH_INTERVAL_MS = 5000;

const rate = (value: number): string => `${value.toFixed(1)}/s`;

type QueueRow = QueueFlowStats & { id: string };

/** Derives a health verdict for a queue from its counts and rates. */
function queueHealth(q: QueueFlowStats): { color: string; label: string } {
  if (q.consumers === 0 && q.messages > 0) return { color: 'red', label: 'No consumer' };
  if (q.redeliverRate > 0.01) return { color: 'orange', label: 'Retrying' };
  if (q.deliverRate > 0.01 || q.messagesUnacked > 0) return { color: 'green', label: 'Consuming' };
  if (q.publishRate > 0.01) return { color: 'blue', label: 'Publishing' };
  return { color: 'gray', label: 'Idle' };
}

interface RateCardProps {
  label: string;
  value: string;
  variant: 'primary' | 'success' | 'info' | 'secondary' | 'danger';
  icon: ReactNode;
}

function RateCard({ label, value, variant, icon }: RateCardProps) {
  return (
    <Grid.Col span={{ base: 6, md: 3 }}>
      <Card variant="gradient" gradientVariant={variant} padding="md">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="white" style={{ opacity: 0.9 }}>
              {label}
            </Text>
            {icon}
          </Group>
          <Text size="xl" fw={700} c="white">
            {value}
          </Text>
        </Stack>
      </Card>
    </Grid.Col>
  );
}

export function MessageFlowTab() {
  const { flow, loading, error, fetchFlow, peekQueue } = useMessageFlow();
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Message inspector state
  const [inspectQueue, setInspectQueue] = useState<string | null>(null);
  const [peeked, setPeeked] = useState<PeekResult | null>(null);
  const [peekLoading, setPeekLoading] = useState(false);
  const [peekError, setPeekError] = useState<string | null>(null);

  useEffect(() => {
    fetchFlow();
  }, [fetchFlow]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchFlow, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchFlow]);

  const handleInspect = useCallback(
    async (queueName: string) => {
      setInspectQueue(queueName);
      setPeeked(null);
      setPeekError(null);
      setPeekLoading(true);
      try {
        const result = await peekQueue(queueName);
        if (result.available) {
          setPeeked(result);
        } else {
          setPeekError(result.error || 'Could not peek this queue');
        }
      } catch (err: any) {
        setPeekError(err?.message || 'Could not peek this queue');
      } finally {
        setPeekLoading(false);
      }
    },
    [peekQueue]
  );

  const rows: QueueRow[] = useMemo(
    () => (flow?.queues ?? []).map((q) => ({ ...q, id: q.name })),
    [flow]
  );

  const columns: DataTableColumn<QueueRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Queue',
        sortable: true,
        render: (value) => (
          <Text size="sm" fw={500}>
            {value}
          </Text>
        ),
      },
      {
        key: 'messagesReady',
        label: 'Ready',
        sortable: true,
        render: (value) => (
          <Badge color={value > 0 ? 'blue' : 'gray'} variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'messagesUnacked',
        label: 'Unacked',
        sortable: true,
        render: (value) => (
          <Badge color={value > 0 ? 'yellow' : 'gray'} variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'consumers',
        label: 'Consumers',
        sortable: true,
        render: (value) => (
          <Badge color={value > 0 ? 'green' : 'red'} variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'publishRate',
        label: 'In',
        sortable: true,
        render: (value) => <Text size="sm">{rate(value)}</Text>,
      },
      {
        key: 'deliverRate',
        label: 'Out',
        sortable: true,
        render: (value) => <Text size="sm">{rate(value)}</Text>,
      },
      {
        key: 'redeliverRate',
        label: 'Retry',
        sortable: true,
        render: (value) => (
          <Text size="sm" c={value > 0.01 ? 'orange' : undefined}>
            {rate(value)}
          </Text>
        ),
      },
      {
        key: 'health',
        label: 'Health',
        render: (_, row) => {
          const h = queueHealth(row);
          return <Badge color={h.color}>{h.label}</Badge>;
        },
      },
      {
        key: 'actions',
        label: '',
        render: (_, row) => (
          <ActionIcon
            variant="subtle"
            onClick={() => handleInspect(row.name)}
            aria-label={`Inspect ${row.name}`}
          >
            <IconEye size={16} />
          </ActionIcon>
        ),
      },
    ],
    [handleInspect]
  );

  if (loading && !flow) {
    return (
      <Group justify="center" p="xl">
        <Loader size="lg" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      {/* Controls */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {flow?.timestamp
            ? `Updated ${new Date(flow.timestamp).toLocaleTimeString('en-ZA')}`
            : 'Not yet loaded'}
          {flow?.overview?.rabbitMqVersion ? ` · RabbitMQ ${flow.overview.rabbitMqVersion}` : ''}
        </Text>
        <Group gap="xs">
          <Button
            variant={autoRefresh ? 'light' : 'subtle'}
            leftSection={autoRefresh ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
            onClick={() => setAutoRefresh((v) => !v)}
          >
            {autoRefresh ? 'Live (5s)' : 'Paused'}
          </Button>
          <Button variant="subtle" leftSection={<IconRefresh size={16} />} onClick={fetchFlow} loading={loading}>
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Management API unavailable */}
      {flow && !flow.available && (
        <Alert color="orange" icon={<IconAlertCircle size={16} />} title="RabbitMQ Management API unavailable">
          {error ||
            'Could not reach the RabbitMQ Management API. Ensure the rabbitmq_management plugin is enabled on the broker.'}
        </Alert>
      )}

      {flow?.available && (
        <>
          {/* Cluster-wide rates */}
          <Grid>
            <RateCard
              label="Published / sec"
              value={rate(flow.overview.publishRate)}
              variant="info"
              icon={<IconArrowUp size={20} color="white" style={{ opacity: 0.85 }} />}
            />
            <RateCard
              label="Delivered / sec"
              value={rate(flow.overview.deliverRate)}
              variant="success"
              icon={<IconArrowDown size={20} color="white" style={{ opacity: 0.85 }} />}
            />
            <RateCard
              label="Acknowledged / sec"
              value={rate(flow.overview.ackRate)}
              variant="primary"
              icon={<IconCircleCheck size={20} color="white" style={{ opacity: 0.85 }} />}
            />
            <RateCard
              label="Redelivered / sec"
              value={rate(flow.overview.redeliverRate)}
              variant={flow.overview.redeliverRate > 0.01 ? 'danger' : 'secondary'}
              icon={<IconRepeat size={20} color="white" style={{ opacity: 0.85 }} />}
            />
          </Grid>

          <Group gap="xl">
            <Text size="sm">
              Waiting for a consumer:{' '}
              <Text span fw={700}>
                {flow.overview.totalReady}
              </Text>
            </Text>
            <Text size="sm">
              In flight (unacked):{' '}
              <Text span fw={700}>
                {flow.overview.totalUnacked}
              </Text>
            </Text>
            <Text size="sm">
              Total messages:{' '}
              <Text span fw={700}>
                {flow.overview.totalMessages}
              </Text>
            </Text>
          </Group>

          {/* Per-queue flow */}
          <Card>
            <DataTable columns={columns} data={rows} striped highlightOnHover />
          </Card>
        </>
      )}

      <MessageInspectorModal
        opened={inspectQueue !== null}
        onClose={() => setInspectQueue(null)}
        title={`Messages in ${inspectQueue ?? ''}`}
        subtitle={
          peeked
            ? `Showing ${peeked.messages.length} of ~${peeked.queueDepth} message(s) currently in the queue`
            : undefined
        }
        messages={peeked?.messages ?? []}
        loading={peekLoading}
        error={peekError}
      />
    </Stack>
  );
}
